import React, { useState, useEffect, useContext } from 'react';
import { Link, useParams } from "react-router-dom";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEllipsisVertical, faAngleDown } from '@fortawesome/free-solid-svg-icons';
import 'bulma/css/bulma.min.css';

import { BeamlineContext } from '../../beamlineProvider.tsx';
import { SampleSetsContext, SampleSetsLoadingState } from '../../sampleSetsProvider.tsx';
import { LoadingBanner, LoadingState } from '../../components/loadingBanner.tsx';
import BeamlineHeader from '../../components/beamlineHeader.tsx';
import DeleteSet from './deleteSet.tsx';
import SampleTable from './sampleTable.tsx';
import { InputEditable, ValidationStatus } from '../../components/inputEditable.tsx';
import { updateSet } from '../../api/api.ts';


const Set: React.FC = () => {

  var { setSlug } = useParams();
  setSlug = setSlug || "";

  const beamlineContext = useContext(BeamlineContext);
  const sampleSetsContext = useContext(SampleSetsContext);

  const [name, setName] = useState<string>("");
  const [description, setDescription] = useState<string>("");

  const [loading, setLoading] = useState<LoadingState>(LoadingState.Loading);
  const [loadingMessage, setLoadingMessage] = useState("");


  useEffect(() => {
    if ((setSlug === undefined) || (!setSlug.trim())) {
      setLoading(LoadingState.Failure);
      setLoadingMessage("Blank Set slug");
      return;
    }

    if (sampleSetsContext.loadingState != SampleSetsLoadingState.Succeeded) {
      setLoading(LoadingState.Loading);
      setLoadingMessage("");
      return;
    }

    const thisSet = sampleSetsContext.sets.getBySlug(setSlug);

    if (thisSet === undefined) {
      setLoading(LoadingState.Failure);
      setLoadingMessage("Set slug not found.");
      return;
    }

    setName(thisSet.name);
    setDescription(thisSet.description);

    setLoading(LoadingState.Success);

  }, [setSlug, sampleSetsContext.changeCounter, sampleSetsContext.loadingState]);

  const set = sampleSetsContext.sets.getBySlug(setSlug);

  // If we're in any loading state other than success, or we can't find our set,
  // display a loading banner instead of the content.
  if ((loading != LoadingState.Success) || !set) {
    return (<LoadingBanner state={loading} message={loadingMessage}></LoadingBanner>)
  }


  async function nameEditValidate(value: string) {
   const trimmed = value.toString().trim();
    if (trimmed.length < 1) {
      return { status: ValidationStatus.Failure, message: "Name cannot be blank." };
    } else if (sampleSetsContext.sets.all().some((c) => c.name == trimmed)) {
      return { status: ValidationStatus.Failure, message: "Name must be unique for ESAF." };
    }
    return { status: ValidationStatus.Success };
  }


  async function nameEditSubmit(value: string) {
    const oldName = set!.name;
    set!.name = value;
    const result = await updateSet({slug: set!.slug, name: value});
    if (result.success) {
      setName(value);
      return { status: ValidationStatus.Success };
    } else {
      set!.name = oldName;
      setName(oldName);
      return { status: ValidationStatus.Failure, message: result.message };
    }
  }


  async function descriptionEditSubmit(value: string) {
    const oldDescription = set!.description;
    set!.description = value;
    const result = await updateSet({slug: set!.slug, description: value});
    if (result.success) {
      setDescription(value);
      return { status: ValidationStatus.Success };
    } else {
      set!.description = oldDescription;
      setDescription(oldDescription);
      return { status: ValidationStatus.Failure, message: result.message };
    }
  }


  return (
    <>
      <BeamlineHeader />

      <nav className="level">
        <div className="level-left">
          <div className="level-item">
            <nav className="breadcrumb is-medium" aria-label="breadcrumbs">
              <ul>
                <li><Link to={ "/" }>ESAFs</Link></li>
                <li><Link to={ "/esaf/" + sampleSetsContext.esaf!.slug }>{ sampleSetsContext.esaf!.name }</Link></li>
                <li className="is-active"><Link to={ "/esaf/" + sampleSetsContext.esaf!.slug + "/set/" + setSlug }>{ set.name }</Link></li>
              </ul>
            </nav>
          </div>
          { sampleSetsContext.esaf!.archived && (
            <div className="level-item">
              <p className="subtitle is-5" style={{ "color": "darkred" }}>(This ESAF is archived.)</p>
             </div>
          )}
        </div>
        <div className="level-right">
          <div className="level-item">

            <div className="dropdown is-right is-hoverable">
              <div className="dropdown-trigger">
                <button className="button" aria-haspopup="true" aria-controls="dropdown-menu-howto">
                  <FontAwesomeIcon icon={faEllipsisVertical} />
                  <span className="icon is-small">
                    <FontAwesomeIcon icon={faAngleDown} />
                  </span>
                </button>
              </div>
              <div className="dropdown-menu" id="dropdown-menu-howto" role="menu">
                <div className="dropdown-content">
                  <DeleteSet />
                </div>
              </div>
            </div>

          </div>
        </div>
      </nav>

      <div className="block">
        <InputEditable
            elementId="sampletable-name"
            value={set.name}
            placeholder={`Name of this ${beamlineContext.setNoun}`}
            showHelp={true}
            editFunctions={{
              validator: nameEditValidate,
              submit: nameEditSubmit
            }} />
        <InputEditable
            elementId="sampletable-description"
            value={set.description}
            placeholder={`Describe this ${beamlineContext.setNoun}`}
            isTextArea={true}
            useCtrlToSave={false}
            showHelp={true}
            textAreaRows={2}
            editFunctions={{
              validator: async () => { return { status: ValidationStatus.Success } },
              submit: descriptionEditSubmit
            }} />
      </div>

      <SampleTable setslug={setSlug} />

    </>
  )
}

export default Set
